import {
  SequenceMenu,
  InputSelectControlField,
  TextInputField,
  RadioButtonGroupField,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { UsageTierType } from 'common/drawers/PricingEditor/domain'
import { useVolumePriceForm } from './useVolumePriceForm'
import { ReadOnlyDetails } from '../common/ReadOnlyDetails'
import { PricingModelField } from '../common/PricingModelField'
import { UsageMetricParameterFields } from '../common/UsageMetrics/UsageMetricParameterFields'
import { FixedTierTableInput } from '../common/inputs/FixedTierTableInput/FixedTierTableInput'
import { PercentageTierTableInput } from '../common/inputs/PercentageTierTableInput/PercentageTierTableInput'
import { PercentageTierWithLimitsTableInput } from '../common/inputs/PercentageTierWithLimitsTableInput/PercentageTierWithLimitsTableInput'
import { kebabMenuItems } from './volumePrice.utils'
import { ExternalLedgerForm } from '../common/ExternalLedgerForm/ExternalLedgerForm'
import {
  ShowAdditionalFieldsToggle,
  useShowAdditionalFields
} from '../common/ShowAdditionalFieldsToggle'
import { ListPriceField } from '../common/ListPriceField'
import { Currency } from '@sequencehq/api'
import { BillingFrequencyField } from '../common/BillingFrequencyField'
import { CopyPricingTableButton } from '../common/CopyPricingTableButton'
import { GreyGrey70, Primary13SemiBold } from '@sequencehq/core-components'
import { UsageMetricSelect } from 'UsageMetrics/index'

export const VolumePriceVariantForm = () => {
  const { fieldsConfig, currency, priceDetails } = useVolumePriceForm()
  const { showAdditionalFields } = useShowAdditionalFields()

  return (
    <Flex direction="column" gap="16px">
      <ListPriceField />
      <ReadOnlyDetails>
        {priceDetails.map(({ label, value }) => (
          <ReadOnlyDetails.Item key={label} label={label} value={value} />
        ))}
      </ReadOnlyDetails>

      <TextInputField
        label="Display name (appears on invoice)"
        data-testid="volume.name"
        value={fieldsConfig.name.value}
        onChange={e => fieldsConfig.name.onChange(e.target.value)}
        validationErrors={fieldsConfig.name.validationErrors}
        isDisabled={fieldsConfig.name.disabled}
        placeholder="Enter a display name"
      />
      <RadioButtonGroupField
        label="Type"
        value={fieldsConfig.usageTierType.value}
        options={fieldsConfig.usageTierType.options}
        onChange={newValue =>
          fieldsConfig.usageTierType.onChange(newValue as UsageTierType)
        }
        validationErrors={fieldsConfig.usageTierType.validationErrors}
        isDisabled={fieldsConfig.usageTierType.disabled}
        data-testid="volume.usageTierType"
        fullWidth
      />
      <Box width="100%">
        <Flex gap={4} alignItems="center" marginBottom="8px">
          <Box {...Primary13SemiBold} color={GreyGrey70}>
            Tiers
          </Box>
          {!fieldsConfig.currency.hidden && (
            <InputSelectControlField
              data-testid="volume.currency"
              value={fieldsConfig.currency.value}
              onChange={newCurrency =>
                fieldsConfig.currency.onChange(newCurrency as Currency)
              }
              options={fieldsConfig.currency.options}
              validationErrors={fieldsConfig.currency.validationErrors}
              disabled={fieldsConfig.currency.disabled}
              placeholder="Select currency"
              width="90px"
            />
          )}
          <Flex grow="1"></Flex>
          {fieldsConfig.usageTierType.value === 'PERCENTAGE' ? (
            <Center height="14px">
              <SequenceMenu
                variant="ghost"
                buttonSize="small"
                alignment="bottom-right"
                items={kebabMenuItems({
                  includePercentageLimits:
                    fieldsConfig.includePercentageLimits.value,
                  onChange: fieldsConfig.includePercentageLimits.onChange,
                  percentageTiers: fieldsConfig.percentageTiers.value,
                  currency
                })}
                data-testid="volume.percentageTier.kebabMenu"
              />
            </Center>
          ) : (
            <CopyPricingTableButton
              data={{ tiers: fieldsConfig.tiers.value, currency }}
              data-testid="volume.fixedTier.copyPricingTable"
            />
          )}
        </Flex>
        {fieldsConfig.usageTierType.value === 'FIXED' ? (
          <FixedTierTableInput
            value={fieldsConfig.tiers.value}
            onChange={fieldsConfig.tiers.onChange}
            isDisabled={fieldsConfig.tiers.disabled}
            currency={currency}
            validationErrors={fieldsConfig.tiers.validationErrors}
          />
        ) : fieldsConfig.includePercentageLimits.value ? (
          <PercentageTierWithLimitsTableInput
            value={fieldsConfig.percentageTiers.value}
            onChange={fieldsConfig.percentageTiers.onChange}
            isDisabled={fieldsConfig.percentageTiers.disabled}
            currency={currency}
            validationErrors={fieldsConfig.percentageTiers.validationErrors}
          />
        ) : (
          <PercentageTierTableInput
            value={fieldsConfig.percentageTiers.value}
            onChange={fieldsConfig.percentageTiers.onChange}
            isDisabled={fieldsConfig.percentageTiers.disabled}
            currency={currency}
            validationErrors={fieldsConfig.percentageTiers.validationErrors}
          />
        )}
      </Box>

      <ShowAdditionalFieldsToggle />
      <Flex
        width="100%"
        display={!showAdditionalFields ? 'none' : 'flex'}
        flexDirection="column"
        gap="16px"
      >
        <PricingModelField />

        <BillingFrequencyField
          data-testid="volume.billingFrequency"
          value={fieldsConfig.billingFrequency.value}
          options={fieldsConfig.billingFrequency.options}
          onChange={fieldsConfig.billingFrequency.onChange}
          validationErrors={fieldsConfig.billingFrequency.validationErrors}
          isDisabled={fieldsConfig.billingFrequency.disabled}
        />
        <UsageMetricSelect
          data-testid="volume.usageMetric"
          validationErrors={fieldsConfig.usageMetricId.validationErrors}
          value={fieldsConfig.usageMetricId.value}
          onChange={fieldsConfig.usageMetricId.onChange}
          isDisabled={fieldsConfig.usageMetricId.disabled}
        />
        <UsageMetricParameterFields
          fieldsConfig={fieldsConfig}
          usageMetricId={fieldsConfig.usageMetricId.value}
        ></UsageMetricParameterFields>

        <ExternalLedgerForm />
      </Flex>
    </Flex>
  )
}
